import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { FILE_TYPES, IErrorsHashMap, IImportSettings, IMappings, IPostAPIRequest, IUserImportUploadSettings } from './users-import.interface';
import { Observable, ReplaySubject, Subject } from 'rxjs';

import * as XLSX from 'xlsx';
import { environment } from '@utility/app.util';
import { doesPhoneNumberExits, filterNumber, removeEmptyStringsFromEnd, splitFullName } from './users-import.utils';
import { isNil, maxBy } from 'lodash';
import { SOCIUU_FIELDS_KEYS, STEPPER_SETTINGS } from './users-import.const';
import { MAX_ROWS } from './users-import.const';
import { canUseToString } from '@app/utility/app.util';

@Injectable({ providedIn: 'root' })
export class UsersImportService {
  private fileSubject = new Subject<File | null>();
  public fileChanged = this.fileSubject.asObservable();
  private fileConversionStatus: Subject<'started' | 'completed' | 'aborted'> = new Subject<'started' | 'completed' | 'aborted'>();
  private static _mappings: Array<IMappings>;
  private static _useDeptAsUsergroup: boolean = false;
  private rowsToSelectHeader: ReplaySubject<Array<Array<string>>> = new ReplaySubject<Array<Array<string>>>(1);

  public static _fullNameSplittingCharc: string = ' ';
  public static importSettings: IImportSettings;
  public static reviewSectionState: { errors: IErrorsHashMap, states: Array<boolean> };
  public static isImportInProgress: boolean = false;
  public fullTableData: Array<Array<any>>;
  public static includeHeaders: boolean;
  public static headerIndex: number;
  public selectedRowDataForHeaders: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public nextSelectedRowDataStepTwo: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public userImportUploadSettings = new BehaviorSubject<IUserImportUploadSettings>(null);
  public importedTableData = new BehaviorSubject<Array<any>>(null);

  public set useDeptAsUsergroup(val: boolean) {
    UsersImportService._useDeptAsUsergroup = val;
  }

  public set setFullNameSplittingCharc(value: string) {
    UsersImportService._fullNameSplittingCharc = value;
  }

  public set mappings(mappingsVal: Array<IMappings>) {
    UsersImportService._mappings = mappingsVal;
    let activeColoumnsNames = mappingsVal.filter(coloumn => coloumn.state && coloumn?.sociuuProp?.key).map(coloumnNames => coloumnNames.userProp);
    let filteredColumnsData: any[][] = this.removeColumns(activeColoumnsNames); // passing coloumns names which must retains

    const includeFullNameColumn = mappingsVal.findIndex(val => val.state && val.sociuuProp.key === SOCIUU_FIELDS_KEYS.FULL_NAME);
    if (includeFullNameColumn !== -1) {
      //does our mappings contains full name?
      const fullNameColumnIndexInFilteredTable = mappingsVal
        .filter(column => column.state && column?.sociuuProp?.key)
        .findIndex(e => e?.sociuuProp?.key === SOCIUU_FIELDS_KEYS.FULL_NAME)
      ;
      filteredColumnsData = this.createFirstAndLastNameColumns(fullNameColumnIndexInFilteredTable, [...filteredColumnsData]);
      filteredColumnsData = this.removeFullNameColumn(undefined, fullNameColumnIndexInFilteredTable, [...filteredColumnsData]);
    }
    let filteredRowsData: any[][] = filteredColumnsData.slice(UsersImportService.headerIndex + 1); // removing the selected header

    const phoneNumberExits: number = doesPhoneNumberExits(UsersImportService._mappings);
    if (phoneNumberExits !== -1) {
      filteredRowsData.forEach((row, index) => {
        if (row[phoneNumberExits] !== undefined) {
          filteredRowsData[index][phoneNumberExits] = filterNumber(row[phoneNumberExits]);
        }
      });
    }

    filteredRowsData.forEach((row, rowIdx) => {
      row.forEach((cell, colIdx) => {
        if (!canUseToString(cell)) return;
        filteredRowsData[rowIdx][colIdx] = cell.toString().trim();
      });
    });

    this.resetSteps([STEPPER_SETTINGS.STEP_FIVE]);
    this.setImportedTableData(filteredRowsData);
  }

  public set header(val: { header: any, nextToHeader: any, headerIndex: number }) {
    const { header, nextToHeader, headerIndex } = val || {};
    this.selectedRowDataForHeaders.next(header);
    this.nextSelectedRowDataStepTwo.next(nextToHeader);
    UsersImportService.headerIndex = headerIndex;
    this.setIncludeHeaders = true;
    header && this.fillEndingEmptyCells();
    this.resetSteps([STEPPER_SETTINGS.STEP_THREE, STEPPER_SETTINGS.STEP_FIVE]);
  }

  public set setIncludeHeaders(val: boolean) {
    UsersImportService.includeHeaders = val;
    this.setUserImportUploadSettings({ ...this.userImportUploadSettings?.value, includeHeaders: val });
  }

  public get mappings(): Array<IMappings> {
    return UsersImportService._mappings;
  }

  public get getFullNameSplittingChar(): string {
    return UsersImportService._fullNameSplittingCharc;
  }

  constructor(private httpClient: HttpClient) {
    this.fileChanged.subscribe({
      next: async (file) => {
        if (!file) return;
        try {
          this.fileConversionStatus.next('started');
          const {fileType, columnSeperatorValue, includeHeaders } = this.userImportUploadSettings.value;
          let arrayData;
          if (fileType === FILE_TYPES.xlsx || fileType === FILE_TYPES.xls) {
            arrayData = (await this.xlsxToArray(file)).map(row => removeEmptyStringsFromEnd(row));
          }
          if (fileType === FILE_TYPES.csv) {
            arrayData = (await this.parseCsvFile(file, columnSeperatorValue)).map(row => removeEmptyStringsFromEnd(row));
          }

          if (arrayData.length > (includeHeaders ? MAX_ROWS+1 : MAX_ROWS)) {
            this.fileConversionStatus.next('aborted');
            this.resetSteps([STEPPER_SETTINGS.STEP_ONE, STEPPER_SETTINGS.STEP_TWO, STEPPER_SETTINGS.STEP_THREE, STEPPER_SETTINGS.STEP_FOUR, STEPPER_SETTINGS.STEP_FIVE]);
            return;
          }

          this.resetSteps([STEPPER_SETTINGS.STEP_TWO, STEPPER_SETTINGS.STEP_THREE, STEPPER_SETTINGS.STEP_FOUR, STEPPER_SETTINGS.STEP_FIVE]);
          this.fullTableData = arrayData;
          await this.createTempHeader();
          this.fileConversionStatus.next('completed');
          this.rowsToSelectHeader.next(arrayData);
          UsersImportService.isImportInProgress = true;
        } catch (error) {
          console.error(error);
        }
      }
    });
  }

  public createTempHeader(): Promise<any> {
    return new Promise(res => {
      const largestRow = maxBy(this.fullTableData, 'length');
      const maxColumns = largestRow.length;
      const tempHeader = new Array(maxColumns);
      for (let i = 0; i < maxColumns; i++) {
        tempHeader[i] = `Column # ${i + 1}`;
      }
      this.header = { header: tempHeader, nextToHeader: largestRow, headerIndex: undefined };
      this.setIncludeHeaders = false;
      res(1);
    });
  }

  public removeColumns(columnsToRetain: string[]): any[][] {
    // Assuming the first row of the table contains column headers
    const headerRow = this.selectedRowDataForHeaders.value;
    // // Find the indices of the columns to be retained
    const retainedColumnIndices = columnsToRetain.map(columnName =>
      headerRow.indexOf(columnName)
    ).filter(index => index !== -1);
    // Reconstruct the table with only the retained columns
    const newTable = this.fullTableData.map(row => {
      const updatedRow: Array<any> = new Array();
      retainedColumnIndices.forEach(index => {
        if (row && !isNil(row[index])) updatedRow.push(row[index]);
      });
      return updatedRow;
    });

    return newTable;
  }

  public removeFullNameColumn(columnsToRetain?: string[], userColumnNameToRemove?: any, dataToFilterFrom?: any[][]): any[][] {
    return dataToFilterFrom.map(e => {
     return e.map((d,i) => {
        if (i !== userColumnNameToRemove) {
          return d;
        }
      }).filter(e => e !== undefined)
    });
  }

  public createFirstAndLastNameColumns(fullNameColumnIndex: number, data: any[][], seperatorChar: string = ' '): any {
    seperatorChar = this.getFullNameSplittingChar;
    let fullNameColumn: Array<any> = data.map((row, index) => {
      return row[fullNameColumnIndex];
    });
    if (UsersImportService.headerIndex !== undefined) {
      fullNameColumn = fullNameColumn.slice(UsersImportService.headerIndex+1);
    }
    const names: Array<{ firstName: string, lastName: string }> = splitFullName(fullNameColumn, seperatorChar);
    const firstNames: Array<string> = new Array(fullNameColumn.length).fill('');
    const lastNames: Array<string> = new Array(fullNameColumn.length).fill('');
    names.forEach((nameVal, index) => {
      firstNames[index] = nameVal.firstName || '';
      lastNames[index] = nameVal.lastName || '';
    });
    let dataWithFirstName = this.addNewColumn('First Name', firstNames, [...data], fullNameColumnIndex+1)
    let dataWithFirsAndLasttName = this.addNewColumn('Last Name', lastNames, [...dataWithFirstName], fullNameColumnIndex+2)
    return [...dataWithFirsAndLasttName];
  }

  public addNewColumn(columnName: string, newColoumnData: Array<any>, data: Array<any>, at: number = -1): any {
    at = UsersImportService._mappings.find(prop => prop.userProp === columnName).index;
    let onlyRows = [...data];
    let currentDataHeader;
    if (UsersImportService.headerIndex !== undefined) {
      onlyRows = data.slice(UsersImportService.headerIndex+1);
      currentDataHeader = data[UsersImportService.headerIndex];
      currentDataHeader.splice(at, 0, columnName);
    }
    // Add the new column to each row of the table.
    onlyRows.forEach((row, index) => {
      // Insert the data at the specified position.
       row.splice(at, 0, newColoumnData[index]);
    });
    // insert the column name into the headers.
    if (this.selectedRowDataForHeaders && this.selectedRowDataForHeaders.value) {
      this.selectedRowDataForHeaders.value.splice(at, 0, columnName);
    }
    if (UsersImportService.headerIndex !== undefined) {
      return [currentDataHeader, ...onlyRows];
    }
    return [...onlyRows];
  }

  private fillEndingEmptyCells(): void {
    const maxHeaderLength: number = this.selectedRowDataForHeaders?.value?.length || 0;
    this.fullTableData = this.fullTableData.map((row, index) => {
      if (maxHeaderLength > row.length) {
        while(row.length < maxHeaderLength) {
          row.push('');
        }
      }
      return row;
    })
  }

  private async xlsxToArray(file: File): Promise<any[][]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', FS: this.userImportUploadSettings.value.columnSeperatorValue });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        resolve((XLSX.utils.sheet_to_json(worksheet, { raw: true, defval: "", header: 1, blankrows: false })));
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }

  private async parseCsvFile(file: File, columnSeparator: string = ',', valueSeparator: string = '\r\n'): Promise<string[][]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const content = e.target.result as string;
        const rows: string[] = content.split(valueSeparator); // Splitting rows
        const filteredRows: string[] = rows.filter((row: string) => !new RegExp(`^[${columnSeparator}\\r]*$`).test(row));
        const result = filteredRows.map(row => row.split(columnSeparator)); // Splitting each row into columns
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }

  public resetAll(): void {
    this.resetSteps([
      STEPPER_SETTINGS.STEP_ONE,
      STEPPER_SETTINGS.STEP_TWO,
      STEPPER_SETTINGS.STEP_THREE,
      STEPPER_SETTINGS.STEP_FOUR,
      STEPPER_SETTINGS.STEP_FIVE,
    ]);
    UsersImportService.isImportInProgress = false;
  }

  private resetSteps(steps: Array<number>): void {
    if (steps.includes(STEPPER_SETTINGS.STEP_ONE)) {
      this.setFile(undefined);
      this.setIncludeHeaders = false;
      this.setUserImportUploadSettings(null);
    }
    if (steps.includes(STEPPER_SETTINGS.STEP_TWO)) {
      this.header = { header: undefined, nextToHeader: undefined, headerIndex: undefined };
    }
    if (steps.includes(STEPPER_SETTINGS.STEP_THREE)) {
      UsersImportService._mappings = undefined;
      this.useDeptAsUsergroup = undefined;
    }
    if (steps.includes(STEPPER_SETTINGS.STEP_FOUR)) {
      UsersImportService.importSettings = undefined;
    }
    if (steps.includes(STEPPER_SETTINGS.STEP_FIVE)) {
      UsersImportService.reviewSectionState = undefined;
      this.setImportedTableData([]); //For review section
    }
  }

  /* ----------- */

  public tarnsformDataForAPI(type: 'analyze' | 'import' = 'import'): Observable<any> {
    UsersImportService.isImportInProgress = true;
    const filteredMappings: Array<IMappings> = UsersImportService._mappings.filter((coloumn: IMappings) =>
      coloumn && coloumn.state &&
      coloumn.sociuuProp?.key &&
      coloumn.sociuuProp?.key !== SOCIUU_FIELDS_KEYS.FULL_NAME
    );

    const data: { data: IPostAPIRequest } = { data: {
      sociuu_columns: filteredMappings.filter(
        coloumn => coloumn.sociuuProp?.key !== SOCIUU_FIELDS_KEYS.FULL_NAME
      ).map(coloumnNames => coloumnNames.sociuuProp.key),
      settings: {
        // sociuu_user_fields_mappings are in the form of [[userProp, sociuuField], [userProp, sociuuField], ....]
        sociuu_user_fields_mappings: filteredMappings.map((mapping: IMappings): Array<string> => ([mapping.userProp, mapping.sociuuProp.key])),
        existing_users: UsersImportService.importSettings.existing_users,
        user_group: UsersImportService.importSettings.user_group,
        remove_existing_users: UsersImportService.importSettings.remove_existing_users,
        user_dept_as_usergroup: undefined,
        user_file_contains_headers: !!UsersImportService.includeHeaders,
        column_separator: this.userImportUploadSettings.value.columnSeperatorValue,
        value_separator: this.userImportUploadSettings.value.valueSeperatorValue,
        ...(type === 'analyze' && UsersImportService.importSettings.remove_existing_users) && {
          remove_existing_users_confirmed: true,
        }
      },
      // in following line we firstly remove the skipped rows, then slice the indexes/serial# we added with each row
      rows: this.importedTableData.value.filter((rows, index) => UsersImportService.reviewSectionState.states[index] === true).map(row => row.slice(1)),
    }};
    data.data.settings.user_dept_as_usergroup = data.data.sociuu_columns.includes(SOCIUU_FIELDS_KEYS.DEPT) ? this.useDeptAsUsergroup : null;

    if (type === 'import') return this.httpClient.post<{ data: IPostAPIRequest }>(`${environment.apiUrl}/v2/users/import`, data);
    else return this.httpClient.post<{ data: IPostAPIRequest }>(`${environment.apiUrl}/v2/users/import/dry-run`, data);
  };

  /* ----------- */

  public setImportedTableData(data: Array<any>): void {
    this.importedTableData.next(data);
  }

  public setFile(file: File | null): void {
    this.fileSubject.next(file);
  }

  public setUserImportUploadSettings(data: IUserImportUploadSettings): void {
    this.userImportUploadSettings.next(data);
  }

  /* ----------- */

  public getFile() {
    return this.fileChanged;
  }

  public getFileConversionStatus(): Observable<'started' | 'completed' | 'aborted'> {
    return this.fileConversionStatus.asObservable();
  }

  public getRowsToSelectHeader(): Observable<Array<Array<string>>> {
    return this.rowsToSelectHeader.asObservable();
  }

  public getSelectedRowIndex(): number {
    return UsersImportService.headerIndex;
  }

  public getSelectedRowDataFromStepTwo(): Observable<any> {
    return this.selectedRowDataForHeaders.asObservable();
  }

  public getSelectedRowDataValueFromStepTwo(): Array<any> {
    return this.selectedRowDataForHeaders.value;
  }

  public getNextSelectedRowDataFromStepTwo(): Observable<any> {
    return this.nextSelectedRowDataStepTwo.asObservable();
  }

  public getUserImportUploadSettings(): Observable<IUserImportUploadSettings> {
    return this.userImportUploadSettings.asObservable();
  }

  public getImportedTableData(): Observable<any> {
    return this.importedTableData.asObservable();
  }

  public get useDeptAsUsergroup(): boolean {
    return  UsersImportService._useDeptAsUsergroup;
  }
}
